import React from "react";
import type { ScorecardWorkbenchUsage } from "../../../../shared/scorecard-workbench";
import type { BaselineFacts } from "../../lib/scorecard-workbench-baseline";

const NUMBER = new Intl.NumberFormat("en-US");

function tokensOf(usage: ScorecardWorkbenchUsage): string {
  const reasoning = usage.reasoning_tokens ?? 0;

  return `${NUMBER.format(usage.input_tokens)} / ${NUMBER.format(
    usage.output_tokens
  )} / ${NUMBER.format(reasoning)} in / out / reasoning`;
}

function costOf(usage: ScorecardWorkbenchUsage): string {
  const dollars = usage.cost_micro_usd / 1_000_000;

  return `$${dollars.toFixed(2)} est · pricing ${usage.pricing_version}`;
}

/**
 * Every separator is a flex child, so the gap around it never depends on
 * whether the text beside it is proportional or monospaced.
 */
function Separator(): React.ReactElement {
  return <span aria-hidden="true">·</span>;
}

/**
 * A baseline whose revision predates usage recording says so. Rendering zero
 * tokens would read as a free Analysis rather than an unrecorded one.
 */
export function FactsLine({
  facts,
  modelLabel,
}: {
  facts: BaselineFacts;
  modelLabel: string;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body text-muted-foreground">
      <span className="text-foreground">{modelLabel}</span>
      <Separator />
      <span>
        Prompt <span className="font-mono">{facts.analysisVersion}</span>
      </span>
      <Separator />
      <span className="font-mono">{facts.elapsed}</span>
      <Separator />
      {facts.usage ? (
        <>
          <span className="font-mono">{tokensOf(facts.usage)}</span>
          <span className="ml-2 font-mono">{costOf(facts.usage)}</span>
        </>
      ) : (
        <span>usage not recorded</span>
      )}
    </div>
  );
}
